<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentStickersAPI from 'dashboard/api/agentStickers';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['send']);

const { t } = useI18n();
const popoverRef = ref(null);
const fileInputRef = ref(null);
const stickers = ref([]);
const selectedFilter = ref('all');
const isLoading = ref(false);
const isUploading = ref(false);

const filters = computed(() => [
  { key: 'all', label: t('CONVERSATION.REPLYBOX.STICKERS.ALL') },
  { key: 'favorites', label: t('CONVERSATION.REPLYBOX.STICKERS.FAVORITES') },
  { key: 'recent', label: t('CONVERSATION.REPLYBOX.STICKERS.RECENT') },
]);

const visibleStickers = computed(() => {
  if (selectedFilter.value === 'favorites') {
    return stickers.value.filter(sticker => sticker.favorite);
  }
  if (selectedFilter.value === 'recent') {
    return stickers.value.filter(sticker => sticker.last_used_at);
  }
  return stickers.value;
});

const showError = () => {
  useAlert(t('CONVERSATION.REPLYBOX.STICKERS.ERROR'));
};

const fetchStickers = async () => {
  isLoading.value = true;
  try {
    const { data } = await AgentStickersAPI.get();
    stickers.value = data;
  } catch (error) {
    showError();
  } finally {
    isLoading.value = false;
  }
};

const openFilePicker = () => fileInputRef.value?.click();

const uploadSticker = async event => {
  const [file] = event.target.files || [];
  event.target.value = '';
  if (!file) return;

  isUploading.value = true;
  try {
    const { data } = await AgentStickersAPI.create(file);
    stickers.value.unshift(data);
  } catch (error) {
    const message =
      error?.response?.data?.message || error?.response?.data?.error;
    useAlert(message || t('CONVERSATION.REPLYBOX.STICKERS.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const toggleFavorite = async sticker => {
  try {
    const { data } = await AgentStickersAPI.update(sticker.id, {
      favorite: !sticker.favorite,
    });
    Object.assign(sticker, data);
  } catch (error) {
    showError();
  }
};

const deleteSticker = async sticker => {
  try {
    await AgentStickersAPI.delete(sticker.id);
    stickers.value = stickers.value.filter(item => item.id !== sticker.id);
  } catch (error) {
    showError();
  }
};

const sendSticker = sticker => {
  sticker.last_used_at = new Date().toISOString();
  emit('send', sticker);
  popoverRef.value?.hide();
};
</script>

<template>
  <Popover ref="popoverRef" align="start" @show="fetchStickers">
    <template #default="{ isOpen }">
      <NextButton
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.STICKERS.OPEN')"
        icon="i-ph-sticker"
        :variant="isOpen ? 'solid' : 'faded'"
        color="slate"
        sm
      />
    </template>

    <template #content>
      <div
        class="flex h-[28rem] w-[22rem] max-w-[calc(100vw-2rem)] flex-col bg-n-solid-2"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak p-3"
        >
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.STICKERS.TITLE') }}
            </h3>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ $t('CONVERSATION.REPLYBOX.STICKERS.PRIVATE_DESCRIPTION') }}
            </p>
          </div>
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept="image/png,image/jpeg,image/gif,image/webp"
            @change="uploadSticker"
          />
          <NextButton
            v-tooltip.top="$t('CONVERSATION.REPLYBOX.STICKERS.ADD')"
            icon="i-ph-plus"
            color="blue"
            sm
            :is-loading="isUploading"
            @click.stop="openFilePicker"
          />
        </div>

        <div class="flex gap-1 border-b border-n-weak px-3 py-2">
          <button
            v-for="filter in filters"
            :key="filter.key"
            type="button"
            class="rounded-md px-2 py-1 text-xs font-medium transition-colors"
            :class="
              selectedFilter === filter.key
                ? 'bg-n-brand text-white'
                : 'text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="selectedFilter = filter.key"
          >
            {{ filter.label }}
          </button>
        </div>

        <div v-if="isLoading" class="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
        <div
          v-else-if="visibleStickers.length"
          class="grid flex-1 auto-rows-min grid-cols-4 gap-2 overflow-y-auto p-3"
        >
          <div
            v-for="sticker in visibleStickers"
            :key="sticker.id"
            class="group relative aspect-square rounded-lg bg-n-alpha-1 p-1 hover:bg-n-alpha-2"
          >
            <button
              v-tooltip.top="sticker.name"
              type="button"
              class="flex size-full items-center justify-center"
              @click="sendSticker(sticker)"
            >
              <img
                :src="sticker.url"
                :alt="sticker.name"
                class="max-h-full max-w-full object-contain"
              />
            </button>
            <button
              v-tooltip.top="$t('CONVERSATION.REPLYBOX.STICKERS.FAVORITE')"
              type="button"
              class="absolute left-0.5 top-0.5 flex size-6 items-center justify-center rounded-full bg-n-solid-2/90 text-n-slate-11 opacity-0 shadow-sm group-hover:opacity-100"
              :class="{ '!opacity-100 !text-n-amber-10': sticker.favorite }"
              @click.stop="toggleFavorite(sticker)"
            >
              <span
                :class="sticker.favorite ? 'i-ph-star-fill' : 'i-ph-star'"
              />
            </button>
            <button
              v-tooltip.top="$t('CONVERSATION.REPLYBOX.STICKERS.DELETE')"
              type="button"
              class="absolute right-0.5 top-0.5 flex size-6 items-center justify-center rounded-full bg-n-solid-2/90 text-n-ruby-9 opacity-0 shadow-sm group-hover:opacity-100"
              @click.stop="deleteSticker(sticker)"
            >
              <span class="i-ph-trash" />
            </button>
          </div>
        </div>
        <div
          v-else
          class="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center text-n-slate-10"
        >
          <span class="i-ph-sticker text-4xl" />
          <p class="mb-0 text-sm">
            {{ $t('CONVERSATION.REPLYBOX.STICKERS.EMPTY') }}
          </p>
          <NextButton
            :label="$t('CONVERSATION.REPLYBOX.STICKERS.ADD')"
            icon="i-ph-plus"
            color="blue"
            sm
            :is-loading="isUploading"
            @click="openFilePicker"
          />
        </div>
      </div>
    </template>
  </Popover>
</template>
